import type { FC } from 'react';
import React, { useEffect, useRef } from 'react';
import { Button, Tooltip, KeyCodes, TooltipBodyDirection } from 'venn-ui-kit';
import { cn } from '@/lib/utils';

export interface ModalFooterProps {
  /** Old behaviour of this component was making various buttons dense / flat by default
   * Design preferred to get rid of this behaviour in new components and simplify the button styles.
   * Keeping the old option for backwards compatibility */
  simplifiedButtonStyles?: boolean;
  submitOnEnter?: boolean; // Allows to fire onPrimaryClick on Enter

  className?: string;

  primaryLabel?: string | JSX.Element;
  primaryClassName?: string;
  primaryDisabled?: boolean;
  onPrimaryClick?: () => void;
  primaryTooltip?: string;
  primaryCustomStyle?: React.CSSProperties;

  secondaryLabel?: string | JSX.Element;
  secondaryClassName?: string;
  secondaryDisabled?: boolean;
  onSecondaryClick?: () => void;
  secondaryDestructive?: boolean;

  cancelClassName?: string;
  onCancel?: () => void;
  cancelLabel?: string | JSX.Element;
  destructive?: boolean;

  leftChildren?: React.ReactNode;
  rightChildren?: React.ReactNode;
  inverted?: boolean;
  tooltipBodyDirection?: TooltipBodyDirection;
}

/** Excludes the id prefix ('#'). */
export const MODAL_FOOTER_PRIMARY_BTN_ID = 'modal-footer-button-primary';

export const ModalFooter: FC<React.PropsWithChildren<ModalFooterProps>> = ({
  simplifiedButtonStyles = false,
  submitOnEnter,
  destructive = false,
  primaryLabel,
  primaryClassName,
  primaryDisabled,
  onPrimaryClick,
  primaryTooltip,
  primaryCustomStyle,
  secondaryLabel,
  secondaryClassName,
  secondaryDisabled,
  secondaryDestructive,
  onSecondaryClick,
  cancelClassName,
  onCancel,
  cancelLabel,
  className,
  leftChildren,
  rightChildren,
  inverted,
  tooltipBodyDirection = TooltipBodyDirection.Center,
}) => {
  // Keep this in the ref for `submitOnEnter` to work:
  // `onKeyDown` is assigned to the event listener on mount, and
  // the ref is needed to pick up the changes in `primaryDisabled`
  const primaryDisabledRef = useRef<boolean>(!!primaryDisabled);
  useEffect(() => {
    if (!!primaryDisabled !== primaryDisabledRef.current) {
      primaryDisabledRef.current = !!primaryDisabled;
    }
  }, [primaryDisabled]);

  const onKeydown = (event: KeyboardEvent) => {
    if (event.keyCode === KeyCodes.Enter && !primaryDisabledRef.current) {
      event.stopPropagation();
      event.preventDefault();
      onPrimaryClick?.();
    }
  };

  useEffect(() => {
    if (submitOnEnter) {
      document.addEventListener('keydown', onKeydown);
    }
    return () => {
      if (submitOnEnter) {
        document.removeEventListener('keydown', onKeydown);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const primaryButton =
    !!primaryLabel && !!onPrimaryClick ? (
      <Tooltip bodyDirection={tooltipBodyDirection} content={primaryTooltip} id="modal-footer-tooltip-primary">
        <Button
          refreshedStyling={simplifiedButtonStyles}
          dense={!simplifiedButtonStyles}
          dominant
          destructive={destructive}
          disabled={primaryDisabled}
          onClick={onPrimaryClick}
          className={primaryClassName}
          data-testid="qa-primary-button"
          id={MODAL_FOOTER_PRIMARY_BTN_ID}
          aria-label={primaryLabel}
          style={primaryCustomStyle}
        >
          {primaryLabel}
        </Button>
      </Tooltip>
    ) : null;

  const secondaryButton =
    !!secondaryLabel && !!onSecondaryClick ? (
      <Button
        refreshedStyling={simplifiedButtonStyles}
        dense={!simplifiedButtonStyles}
        destructive={secondaryDestructive}
        disabled={secondaryDisabled}
        onClick={onSecondaryClick}
        className={secondaryClassName}
        data-testid="qa-secondary-button"
      >
        {secondaryLabel}
      </Button>
    ) : null;

  const cancelButton = onCancel ? (
    <Button
      refreshedStyling={simplifiedButtonStyles}
      onClick={onCancel}
      className={cn(['normal-case tracking-[normal]', simplifiedButtonStyles ? '' : 'text-[14px]', cancelClassName])}
      data-testid="qa-close-button"
      type="button"
    >
      {cancelLabel ?? 'Cancel'}
    </Button>
  ) : null;

  return (
    <footer
      id="modal-footer-wrapper"
      className={cn([
        'flex justify-between items-center p-[20px] mt-[24px] h-[50px]',
        'border-0 border-t border-solid border-t-venn-grey-90',
        inverted ? 'bg-transparent' : 'bg-venn-grey-20',
        className,
      ])}
      data-testid="modal-footer"
    >
      <div
        id="modal-footer-left"
        className="flex items-center [&>div]:mr-[10px] [&>div]:ml-0 [&_button]:mr-[10px] [&_button]:ml-0"
      >
        {cancelButton}
        {leftChildren && <div>{leftChildren}</div>}
      </div>
      <div
        id="modal-footer-right"
        className={cn([
          'flex items-center [&_button]:mr-0 [&_button]:ml-[10px]',
          // TODO: see if we can delete this selector for divs
          '[&>div>div]:mr-0 [&>div>div]:ml-[10px]',
        ])}
      >
        {rightChildren && <div>{rightChildren}</div>}
        {secondaryButton}
        {primaryButton}
      </div>
    </footer>
  );
};

export default ModalFooter;
